/* ET PDP Product — gallery, variant selection, upgrade toggle, accordions.

   Config arrives from the section as a JSON island (`[data-et-pdp-data]`) rather than inlined Liquid, so this file
   can live in assets/ and be cached. Init is scoped per section root, so several instances can coexist.

   Accordion note: the old implementation animated `max-height: 0 -> 800px`. That reads as janky because the
   transition duration is spent on a distance the content never uses (short panels snap, then idle) and anything
   over 800px was silently clipped. This animates the real measured height instead and hands back to `auto` once
   the transition lands, so open panels reflow freely. */

(function () {
  'use strict';

  var OPEN_MS = 380;
  var CLOSE_MS = 300;
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------------------ *
   * Accordions
   * ------------------------------------------------------------------ */

  /* Each panel owns its in-flight transition so a fast double-click can't leave a half-open panel behind. */
  function clearPending(body) {
    if (body.__etAccordionEnd) {
      body.removeEventListener('transitionend', body.__etAccordionEnd);
      body.__etAccordionEnd = null;
    }
    if (body.__etAccordionTimer) {
      clearTimeout(body.__etAccordionTimer);
      body.__etAccordionTimer = null;
    }
  }

  function settleOpen(body) {
    /* Back to auto so the panel keeps working if its content reflows (image loads, font swap, window resize). */
    body.style.height = 'auto';
    body.style.overflow = '';
    body.style.transition = '';
  }

  function openPanel(item, body) {
    clearPending(body);
    item.classList.add('is-open');

    if (prefersReducedMotion()) {
      settleOpen(body);
      return;
    }

    var start = body.offsetHeight;
    var target = body.scrollHeight;
    body.style.overflow = 'hidden';
    body.style.transition = 'none';
    body.style.height = start + 'px';
    void body.offsetHeight; /* flush, so the next height change is a transition and not a jump */
    body.style.transition = 'height ' + OPEN_MS + 'ms ' + EASE;
    body.style.height = target + 'px';

    var done = function (e) {
      if (e && e.target !== body) return;
      clearPending(body);
      settleOpen(body);
    };
    body.__etAccordionEnd = done;
    body.addEventListener('transitionend', done);
    /* transitionend can be skipped when the tab is backgrounded mid-animation. */
    body.__etAccordionTimer = setTimeout(done, OPEN_MS + 80);
  }

  function closePanel(item, body) {
    clearPending(body);
    item.classList.remove('is-open');

    if (prefersReducedMotion()) {
      body.style.height = '';
      body.style.overflow = '';
      body.style.transition = '';
      return;
    }

    /* From `auto` there is nothing to interpolate — pin the measured height first, then collapse. */
    body.style.overflow = 'hidden';
    body.style.transition = 'none';
    body.style.height = body.scrollHeight + 'px';
    void body.offsetHeight;
    body.style.transition = 'height ' + CLOSE_MS + 'ms ' + EASE;
    body.style.height = '0px';

    var done = function (e) {
      if (e && e.target !== body) return;
      clearPending(body);
      body.style.transition = '';
      /* Hand the resting state back to the stylesheet (`height: 0`) rather than leaving an inline pixel value.
         If the transition was interrupted — tab backgrounded, animation cancelled — this still lands closed. */
      body.style.height = '';
    };
    body.__etAccordionEnd = done;
    body.addEventListener('transitionend', done);
    body.__etAccordionTimer = setTimeout(done, CLOSE_MS + 80);
  }

  /* Desktop sticky gallery: hand the stylesheet the gallery's own height and the fixed add-to-cart bar's height, so a
     gallery that fits pins under the header and a taller one pins once its last row is in view (see STICKY GALLERY in
     et-pdp-product.css; CSS cannot read an element's own height in `top`). The ResizeObserver fires again when a
     variant change hides or shows slides, when images load and at breakpoint changes. Below 768px the media query
     ignores the class. The bar keeps its height while hidden (visibility + translate), so it can be read up front. */
  function initStickyGallery(root) {
    var col = root.querySelector('.et-pdp__gallery-col');
    if (!col || !('ResizeObserver' in window)) return;

    var bar = document.querySelector('.et-sticky-atc__bar');

    /* The bar's height is only reserved while the bar is showing; before it slides in, a gallery that just fits under
       the header pins there instead of tucking its first row behind the header for nothing. */
    function sync() {
      var barShown = !!bar && bar.classList.contains('is-visible');
      col.style.setProperty('--et-pdp-gallery-h', Math.ceil(col.getBoundingClientRect().height) + 'px');
      col.style.setProperty('--et-pdp-sticky-bottom', ((barShown ? bar.offsetHeight : 0) + 24) + 'px');
    }

    new ResizeObserver(sync).observe(col);
    if (bar && 'MutationObserver' in window) {
      new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['class'] });
    }
    window.addEventListener('resize', sync);
    sync();
    col.classList.add('is-sticky');
  }

  /* ------------------------------------------------------------------ *
   * WebwinkelKeur review popup
   * ------------------------------------------------------------------ */

  /* Clicking the rating reopens the WebwinkelKeur review popup, as it did before the redesign. The popup is NOT
     rebuilt here: the WWK sidebar app embed (shopify://apps/webwinkelkeur/blocks/sidebar) already renders it on
     every page, and its own rating element is what carries the handler - the sidebar's Preact tree binds
     `onClick: () => reviews.show()` to .wwk-sidebar__balloon (compact layout) or .wwk-sidebar__rating-wrapper
     (summary layout). Forwarding a click into that element is what the old theme's <valued-widget> did too.
     Opening the modal directly would mean hard-coding dashboard.webwinkelkeur.nl and the shop id into the theme,
     which breaks the day either changes.

     The target is resolved at click time, not at init: the sidebar script is async and mounts well after this
     runs. The trigger itself starts hidden and is revealed only once that element exists, so it is never a dead
     click target where the sidebar does not render - its config switches the mobile sidebar off. */
  var WWK_REVIEWS_SELECTOR = '.wwk-sidebar__balloon, .wwk-sidebar__rating-wrapper';

  /* Where the app puts no sidebar on the page the theme frames the same reviews page itself (see the markup in
     et-pdp-product.liquid). Measured over CDP on the live storefront: under 909px the app renders nothing at all,
     so on mobile there is simply no element to forward a click into. */
  function initReviewPopup(root) {
    var triggers = qsa(root, '[data-et-wwk-reviews]');
    if (!triggers.length) return;

    var modal = root.querySelector('[data-et-wwk-modal]');
    var frame = modal && modal.querySelector('[data-et-wwk-frame]');
    var opener = null;

    function sidebarRating() {
      return document.querySelector(WWK_REVIEWS_SELECTOR);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') closeModal();
    }

    function closeModal() {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (opener) opener.focus();
    }

    function openModal() {
      if (!modal || !frame) return;
      /* The URL waits in data-src until the first open, so the reviews page is not fetched on every product view. */
      if (!frame.getAttribute('src')) frame.setAttribute('src', frame.getAttribute('data-src') || '');
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      var close = modal.querySelector('[data-et-wwk-close]');
      if (close) close.focus();
    }

    if (modal) {
      qsa(modal, '[data-et-wwk-close], [data-et-wwk-overlay]').forEach(function (el) {
        el.addEventListener('click', closeModal);
      });
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        opener = trigger;
        /* The app's own popup wins wherever it exists, so desktop behaviour is untouched. */
        var target = sidebarRating();
        if (target) target.click();
        else openModal();
      });
    });

    function reveal() {
      if (!sidebarRating() && !modal) return false;
      triggers.forEach(function (trigger) { trigger.hidden = false; });
      return true;
    }

    if (reveal() || !('MutationObserver' in window)) return;

    var observer = new MutationObserver(function () {
      if (reveal()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    /* Third-party script: if it is blocked or fails, stop watching rather than observing the whole document for
       the life of the page. */
    setTimeout(function () { observer.disconnect(); }, 15000);
  }

  function initAccordions(root) {
    qsa(root, '[data-accordions]').forEach(function (group) {
      var items = qsa(group, '.et-pdp__accordion');

      items.forEach(function (item) {
        var btn = item.querySelector('[data-accordion-toggle]');
        var body = item.querySelector('[data-accordion-body]');
        if (!btn || !body) return;

        /* Whichever panel is marked open in the markup starts at auto; the rest stay collapsed by the CSS. */
        if (item.classList.contains('is-open')) settleOpen(body);

        /* The whole accordion toggles — header row and open answer alike — not just the header text.
           Listening on the item (rather than the button) means the button's own click simply bubbles up, so mouse
           and keyboard both route through one handler. */
        item.addEventListener('click', function (e) {
          /* Let real links and any other control inside the answer do their own thing. */
          if (e.target.closest('a, button:not([data-accordion-toggle]), input, select, textarea, label')) return;

          /* Don't collapse the panel out from under someone who was selecting text in the answer. */
          var selection = window.getSelection && window.getSelection();
          if (selection && String(selection) && item.contains(selection.anchorNode)) return;

          var willOpen = !item.classList.contains('is-open');

          /* Auto-close every sibling, animated, so only one panel is ever open. */
          items.forEach(function (other) {
            if (other === item) return;
            if (!other.classList.contains('is-open')) return;
            var otherBody = other.querySelector('[data-accordion-body]');
            var otherBtn = other.querySelector('[data-accordion-toggle]');
            if (otherBody) closePanel(other, otherBody);
            if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
          });

          if (willOpen) openPanel(item, body);
          else closePanel(item, body);
          btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Section
   * ------------------------------------------------------------------ */

  function initSection(root) {
    if (!root || root.__etPdpInit) return;
    root.__etPdpInit = true;

    initAccordions(root);
    initStickyGallery(root);
    initReviewPopup(root);

    var dataEl = root.querySelector('[data-et-pdp-data]');
    if (!dataEl) return;

    var cfg;
    try {
      cfg = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }

    var variants = cfg.variants || [];
    var optionNames = cfg.options || [];
    var selected = (cfg.selected || []).slice();
    var moneyFormat = cfg.moneyFormat || '{{amount}}';

    function formatMoney(cents) {
      // The design writes prices WITHOUT a thousands separator (Figma 1899:43009 -> "1156,00"),
      // and the Liquid side strips it from every `| money` call, so JS must group the same way:
      // not at all. Anything else puts "1.695,65" next to "1695,65" on the same screen.
      var plain = (cents / 100).toFixed(2);
      var intPart = plain.split('.')[0];
      var decPart = plain.split('.')[1];
      var amount = intPart + '.' + decPart;
      var withComma = intPart + ',' + decPart;
      var whole = Math.floor(cents / 100);
      var wholeDotted = String(whole);
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, wholeDotted);
    }

    function matchedVariant() {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < selected.length; j++) {
          if (v.options[j] !== selected[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    /* Cheapest variant carrying `value` at option index `pos` — used for the upgrade toggle's price delta. */
    function variantWith(pos, value) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].options[pos] === value) return variants[i];
      }
      return null;
    }

    /* ---- Gallery ---- */
    var track = root.querySelector('[data-gallery-track]');
    var allSlides = qsa(root, '[data-slide-index]');
    var allThumbs = qsa(root, '[data-thumb-index]');
    var thumbStrip = root.querySelector('[data-gallery-thumbs]');
    var allDots = qsa(root, '[data-dot-index]');
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    var visibleIndices = allSlides.map(function (_, i) { return i; });
    var currentVisible = 0;

    /* A slide matches when every non-empty option filter it carries equals the selected option.
       Untagged media (lifestyle shots) always matches. */
    function slideMatchesVariant(slide) {
      for (var i = 0; i < 3; i++) {
        var filter = slide.getAttribute('data-variant-opt-' + i) || '';
        if (!filter) continue;
        if (filter !== (selected[i] || '')) return false;
      }
      return true;
    }

    /* Keep the active thumbnail inside the strip's scroll port. Without this the highlight simply disappeared once
       navigation passed the last thumbnail that fits: 736px of gallery column holds six 108px thumbs, a product with
       more photos parked the rest outside the port, and with `scrollbar-width: none` and no wheel handling there was
       no way to bring them back - it read as "the active image is not marked". Measured with getBoundingClientRect
       rather than offsetLeft (the strip is not the offsetParent) and scrolled on the container itself rather than
       with scrollIntoView(), which would also scroll the page and fight the sticky gallery. */
    function revealThumb(thumb) {
      if (!thumb || !thumbStrip) return;
      if (thumbStrip.scrollWidth <= thumbStrip.clientWidth + 1) return; /* wrapped layout: nothing to scroll */
      var sr = thumbStrip.getBoundingClientRect();
      var tr = thumb.getBoundingClientRect();
      var delta = 0;
      if (tr.left < sr.left) delta = tr.left - sr.left - 16;
      else if (tr.right > sr.right) delta = tr.right - sr.right + 16;
      if (!delta) return;
      if (thumbStrip.scrollBy) {
        thumbStrip.scrollBy({ left: delta, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } else {
        thumbStrip.scrollLeft += delta;
      }
    }

    function goToVisibleSlide(idx) {
      if (visibleIndices.length === 0) return;
      if (idx < 0) idx = visibleIndices.length - 1;
      if (idx >= visibleIndices.length) idx = 0;
      currentVisible = idx;
      var activeOriginal = visibleIndices[currentVisible];
      if (track) track.style.transform = 'translateX(-' + (currentVisible * 100) + '%)';
      allSlides.forEach(function (s, i) { s.classList.toggle('is-active', i === activeOriginal); });
      allThumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === activeOriginal); });
      allDots.forEach(function (d, i) { d.classList.toggle('is-active', i === activeOriginal); });
      revealThumb(allThumbs[activeOriginal]);
    }

    function applyVariantFilter() {
      var matched = [];
      allSlides.forEach(function (slide, i) {
        if (slideMatchesVariant(slide)) matched.push(i);
      });
      /* Never leave an empty gallery: fall back to showing everything. */
      visibleIndices = matched.length > 0 ? matched : allSlides.map(function (_, i) { return i; });

      var visibleSet = {};
      visibleIndices.forEach(function (i) { visibleSet[i] = true; });
      allSlides.forEach(function (s, i) { s.style.display = visibleSet[i] ? '' : 'none'; });
      allThumbs.forEach(function (t, i) { t.style.display = visibleSet[i] ? '' : 'none'; });
      allDots.forEach(function (d, i) { d.style.display = visibleSet[i] ? '' : 'none'; });

      goToVisibleSlide(0);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goToVisibleSlide(currentVisible - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToVisibleSlide(currentVisible + 1); });

    allThumbs.forEach(function (thumb, originalIdx) {
      thumb.addEventListener('click', function () {
        var visibleIdx = visibleIndices.indexOf(originalIdx);
        if (visibleIdx >= 0) goToVisibleSlide(visibleIdx);
      });
    });

    allDots.forEach(function (dot, originalIdx) {
      dot.addEventListener('click', function () {
        var visibleIdx = visibleIndices.indexOf(originalIdx);
        if (visibleIdx >= 0) goToVisibleSlide(visibleIdx);
      });
    });

    /* Swipe with Pointer Events, so a mouse drags the carousel as well as a finger. Touch-only listeners left it
       frozen wherever there is no touch: the theme editor's mobile preview, a narrow desktop window, a trackpad.
       `touch-action: pan-y` (CSS) keeps vertical page scrolling native and hands horizontal swipes to this code. */
    var mainGallery = root.querySelector('[data-gallery-main]');
    if (mainGallery) {
      var swipe = null;
      var swiped = false;
      mainGallery.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        swipe = { x: e.clientX, y: e.clientY };
        swiped = false;
      });
      mainGallery.addEventListener('pointerup', function (e) {
        if (!swipe) return;
        var dx = e.clientX - swipe.x;
        var dy = e.clientY - swipe.y;
        swipe = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          swiped = true;
          goToVisibleSlide(currentVisible + (dx < 0 ? 1 : -1));
        }
      });
      mainGallery.addEventListener('pointercancel', function () { swipe = null; });
      /* A mouse drag must not start the browser's own image drag, nor end in a click on the slide. */
      mainGallery.addEventListener('dragstart', function (e) { e.preventDefault(); });
      mainGallery.addEventListener('click', function (e) {
        if (swiped) { e.preventDefault(); e.stopPropagation(); swiped = false; }
      }, true);
    }

    /* ---- Shared variant application (swatches + upgrade toggle both route through this) ---- */
    function syncSwatches() {
      qsa(root, '.et-pdp__swatch').forEach(function (sw) {
        var pos = parseInt(sw.getAttribute('data-option-position'), 10) - 1;
        var val = sw.getAttribute('data-option-value');
        sw.classList.toggle('is-selected', selected[pos] === val);
      });
      qsa(root, '[data-option-label]').forEach(function (label) {
        var name = label.getAttribute('data-option-label');
        var idx = optionNames.indexOf(name);
        if (idx >= 0) label.textContent = selected[idx] || '';
      });
    }

    function syncUpgradeToggles() {
      qsa(root, '[data-upgrade-toggle]').forEach(function (toggle) {
        var pos = parseInt(toggle.getAttribute('data-option-position'), 10) - 1;
        var baseVal = toggle.getAttribute('data-base-value');
        var upVal = toggle.getAttribute('data-upgrade-value');
        var isOn = selected[pos] === upVal;

        toggle.classList.toggle('is-on', isOn);
        toggle.setAttribute('aria-checked', isOn ? 'true' : 'false');

        var card = toggle.closest('.et-pdp__upgrade-card');
        var priceEl = card ? card.querySelector('[data-upgrade-price]') : null;
        if (!priceEl) return;
        var baseV = variantWith(pos, baseVal);
        var upV = variantWith(pos, upVal);
        if (baseV && upV) {
          var diff = parseInt(upV.price, 10) - parseInt(baseV.price, 10);
          priceEl.textContent = (diff < 0 ? '-' : '+') + formatMoney(Math.abs(diff));
        } else {
          priceEl.textContent = '';
        }
      });
    }

    function syncPrice() {
      var v = matchedVariant();
      if (!v) return;

      var priceEl = root.querySelector('.et-pdp__price');
      if (priceEl) priceEl.innerHTML = formatMoney(v.price);

      // Keep the 0%-interest instalment in step with the chosen variant. It previously came from
      // Liquid's product.price (the MINIMUM variant) and never updated, understating the figure.
      var instEl = root.querySelector('[data-et-pdp-installment]');
      if (instEl) {
        var tpl = instEl.getAttribute('data-installment-tpl') || '';
        if (tpl) instEl.textContent = tpl.replace('[price]', formatMoney(Math.round(v.price / 3)));
      }

      var compareEl = root.querySelector('.et-pdp__compare-price');
      var badgeEl = root.querySelector('.et-pdp__discount-badge');
      if (v.compare_at_price && v.compare_at_price > v.price) {
        if (compareEl) {
          compareEl.innerHTML = formatMoney(v.compare_at_price);
          compareEl.style.display = '';
        }
        if (badgeEl) {
          badgeEl.textContent = '-' + Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100) + '%';
          badgeEl.style.display = '';
        }
      } else {
        if (compareEl) compareEl.style.display = 'none';
        if (badgeEl) badgeEl.style.display = 'none';
      }

      var ctaBtn = root.querySelector('.et-pdp__cta-btn');
      if (ctaBtn) {
        ctaBtn.setAttribute('data-variant-id', v.id);
        if (typeof window.etUpdateDeliveryAccordion === 'function') window.etUpdateDeliveryAccordion(v);
        /* A sold-out variant has to LOOK sold out, not just refuse the click. The button used to be
           disabled and nothing else: it kept the orange fill and kept reading "In winkelwagen", so
           the only feedback was that tapping it did nothing.

           `is-disabled` is the class the server already stamps for a variant that loads sold out
           (see the button in sections/et-pdp-product.liquid) - the old `is-unavailable` matched no
           rule anywhere, which is why switching to a sold-out size changed nothing visually. */
        var soldOut = v.available === false;
        ctaBtn.disabled = soldOut;
        ctaBtn.classList.toggle('is-disabled', soldOut);
        ctaBtn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');

        var ctaLabel = soldOut
          ? ctaBtn.getAttribute('data-cta-label-sold-out')
          : ctaBtn.getAttribute('data-cta-label');
        /* Only when the theme actually gave us the wording - never blank the button. */
        if (ctaLabel) ctaBtn.textContent = ctaLabel;
      }
    }

    function applyVariant() {
      syncSwatches();
      syncUpgradeToggles();
      syncPrice();
      applyVariantFilter();
    }

    qsa(root, '.et-pdp__swatch').forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        var pos = parseInt(this.getAttribute('data-option-position'), 10) - 1;
        selected[pos] = this.getAttribute('data-option-value');
        applyVariant();
      });
    });

    qsa(root, '[data-upgrade-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var pos = parseInt(this.getAttribute('data-option-position'), 10) - 1;
        var baseVal = this.getAttribute('data-base-value');
        var upVal = this.getAttribute('data-upgrade-value');
        selected[pos] = selected[pos] === upVal ? baseVal : upVal;
        applyVariant();
      });
    });

    /* ---- Add to cart ---- */
    var ctaBtn = root.querySelector('.et-pdp__cta-btn');
    if (ctaBtn) {
      ctaBtn.addEventListener('click', function () {
        var variantId = this.getAttribute('data-variant-id');
        if (!variantId || ctaBtn.classList.contains('is-loading')) return;
        ctaBtn.classList.add('is-loading');

        fetch(cfg.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
          .then(function (r) {
            if (!r.ok) {
              // fetch() only rejects on network failure, so a 422 (bad variant), 429 (rate limit)
              // or any 5xx would otherwise navigate the shopper to an UNCHANGED cart, silently.
              return r.json().catch(function () { return {}; }).then(function (err) {
                throw new Error(err.description || err.message || cfg.cartAddError || 'Could not add to cart.');
              });
            }
            window.location.href = cfg.cartUrl;
          })
          .catch(function (e) {
            ctaBtn.classList.remove('is-loading');
            // The message target is CREATED here rather than assumed: the section markup never
            // rendered a [data-et-pdp-cart-error] node, so the previous lookup always returned null
            // and every add-to-cart failure (422 stock, 429 throttle, 5xx) was completely silent.
            var host = ctaBtn.parentNode;
            if (!host) return;
            var msg = host.querySelector('[data-et-pdp-cart-error]');
            if (!msg) {
              msg = document.createElement('p');
              msg.setAttribute('data-et-pdp-cart-error', '');
              msg.className = 'et-pdp__cart-error';
              msg.setAttribute('role', 'alert');
              msg.style.cssText = 'margin:8px 0 0;color:#c81e1e;font-size:14px;line-height:1.4;';
              host.appendChild(msg);
            }
            msg.textContent = (e && e.message) ? e.message : (cfg.cartAddError || 'Could not add to cart.');
            msg.hidden = false;
          });
      });
    }

    applyVariant();
  }

  function boot() {
    qsa(document, '[data-et-pdp-root]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor support. */
  document.addEventListener('shopify:section:load', function (e) {
    qsa(e.target, '[data-et-pdp-root]').forEach(initSection);
  });
})();
